/* eslint-disable max-lines-per-function */
import { State, useHookstate, type StateMethods } from '@hookstate/core';
import { Box, Dialog, TextField, TextFieldProps, Button, FormControl, Typography, CircularProgress, DialogTitle, Autocomplete, DialogContent, DialogActions } from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '~/hooks/useLang';
import notify from '~/utils/notify';
import { cancelTransferDevice, checkTransferDevice, trasnferDeviceForDispatchGroup, getWorkSchedules } from '../../../services';
import { LoadingButton } from '@mui/lab';
import userUserState from '~/features/User/stores/UserState';
import * as zod from 'zod';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { DispatchGroupResponse } from '~/components/FilterAutocompleteDispatchGroup/interface';
import { getDispatchGroup } from '~/components/FilterAutocompleteDispatchGroup/service';
export interface DeviceTransferError {
  readonly model: string | readonly string[] | null,
  readonly message: string | null
}
interface Field {
  readonly name: string,
  readonly model: string,
  readonly type?: 'textField' | 'select' | 'date',
  readonly required?: boolean,
  readonly defaultValue?: string | null
}
interface DeviceTransferDialogProps {
  readonly title: string,
  readonly fields: ReadonlyArray<Field>,
  readonly deviceId: number | null,
  readonly deviceName: string | null,
  readonly isOpen: State<boolean>
  readonly agencyId?: number | null,
  readonly dispatchGroup?: string | null,
  readonly dispatchGroupId?: number | null,
  readonly forceRefresh?: StateMethods<boolean>;
  readonly handleLoadAllVehicles?: () => void

}

const transferDeviceSchema = zod.object({
  dispatchGroupId: zod.number({ message: 'Select a dispatch group' }),
  destinyName: zod.string().nullable(),
  dateTobeReturned: zod.coerce.date().nullable().default(null)
})

type transferDeviceFormData = zod.infer<typeof transferDeviceSchema>

export function DeviceTransferDialog({ fields, title, deviceId, deviceName, isOpen, agencyId, dispatchGroup, dispatchGroupId, forceRefresh, handleLoadAllVehicles, }: DeviceTransferDialogProps) {
  const { translate } = useLang();
  const error = useHookstate<DeviceTransferError>({ model: null, message: null });
  const canTransferDevice = useHookstate(false);
  const isLoading = useHookstate(false);
  const isSending = useHookstate(false);
  const destinyNameState = useHookstate<string | null>(null);
  const openState = useHookstate(isOpen);
  const isShowCancelScheduleButton = useHookstate(false);
  const errorCheckTransfer = useHookstate('');
  const [optionsFilter, setOptionsFilter] = useState<readonly DispatchGroupResponse[]>([]);
  const textFieldsRef = useRef<Record<string, TextFieldProps>>({});
  const dispatchGroupIdSelected = useHookstate<number | null>(null);
  const hasWorkSchedule = useHookstate(false);
  const showConfirmDialog = useHookstate(false);
  const pendingFormData = useHookstate<transferDeviceFormData | null>(null);

  const { register, resetField, handleSubmit, control, watch, setValue, setError, clearErrors, formState: { errors } } = useForm<transferDeviceFormData>({
    resolver: zodResolver(transferDeviceSchema),
    criteriaMode: "all",
    defaultValues: {
      dateTobeReturned: null,
      destinyName: null,
    },
  })

  async function onSubmitForm(data: transferDeviceFormData) {
    if (!data.dispatchGroupId) {
      setError("dispatchGroupId", {
        types: {
          required: "This is required",
          message: "Campo obrigatório"
        }
      });
      return false;
    }

    if (!deviceId) {
      error.message.set(translate('No Device Id'));
      return false;
    }

    // Verificar se tem escala e ainda não confirmou
    if (hasWorkSchedule.get() && !pendingFormData.get()) {
      pendingFormData.set(data);
      showConfirmDialog.set(true);
      return false;
    }

    await executeTransfer(data);
  }

  async function executeTransfer(data: transferDeviceFormData) {
    isSending.set(true);

    trasnferDeviceForDispatchGroup({
      deviceId: deviceId!,
      destDispatchGroupId: data.dispatchGroupId,
      destinyName: data.destinyName,
      dateTobeReturned: dayjs(data.dateTobeReturned)
    }).then((response) => {
      if (response.status === 200) {
        notify({
          message: translate('Device was transferred successfully!'),
          type: 'success'
        });
        resetField('dispatchGroupId');
        resetField('dateTobeReturned');
        resetField('destinyName');
        forceRefresh?.set(!forceRefresh.get());
        openState.set(false);
      }
    }).finally(() => {
      openState.set(false);
      isSending.set(false);
      isLoading.set(false);
      pendingFormData.set(null);
      showConfirmDialog.set(false);
    });
  }

  function handleConfirmTransfer() {
    const data = pendingFormData.get();
    if (data) {
      executeTransfer(data);
    }
  }

  function handleCancelConfirm() {
    showConfirmDialog.set(false);
    pendingFormData.set(null);
    isSending.set(false);
  }

  useEffect(() => {
    resetField('dateTobeReturned');
    clearErrors('dispatchGroupId');

    if (deviceId && isOpen.get()) {
      checkDeviceIsAlreadyToTransfer();
      checkDeviceWorkSchedule();

      getDispatchGroup().then(dispatchGroup => {
        setOptionsFilter(dispatchGroup);
      });
    }
  }, [deviceId, isOpen.get()]);

  async function onCancelScheduleTransfer() {
    isSending.set(true);

    cancelTransferDevice({ deviceId: deviceId }).then((response) => {
      if (response.status === 200) {
        notify({
          message: translate('Schedule to device was canceled successfully!'),
          type: 'success'
        });
      }
    }).finally(() => {
      openState.set(false)
      isSending.set(false)
      isLoading.set(false)
    });
  };

  const checkDeviceIsAlreadyToTransfer = React.useCallback(async () => {
    isLoading.set(true);
    errorCheckTransfer.set('');

    checkTransferDevice({ deviceId: deviceId }).then((response) => {
      if (response.status === 200) {
        const checkDevice = response.data as { readonly authorized: boolean; readonly error: string; readonly scheduled: boolean; };
        canTransferDevice.set(checkDevice.authorized === true);
        isShowCancelScheduleButton.set(checkDevice.scheduled === true);

        if (checkDevice.error) {
          errorCheckTransfer.set(checkDevice.error);
        }
      }

    }).catch((err) => {
      errorCheckTransfer.set(err);
    }).finally(() => {
      setTimeout(() => {
        isLoading.set(false)
      }, 500)
    });
  }, [deviceId]);

  async function checkDeviceWorkSchedule() {
    if (!deviceId) return;

    try {
      const response = await getWorkSchedules({ deviceId });

      if (response?.status === 200 && response?.data) {
        const data = response.data;

        // Compatibilidade com o formato antigo (array) e o novo (objeto com current_work_schedule e next_work_schedule)
        if (Array.isArray(data)) {
          hasWorkSchedule.set(data.length > 0);
        } else {
          const hasCurrent = !!data.current_work_schedule;
          const hasNext = !!data.next_work_schedule;

          hasWorkSchedule.set(hasCurrent || hasNext);
        }
      } else {
        hasWorkSchedule.set(false);
      }
    } catch (err) {
      hasWorkSchedule.set(false);
    }
  }

  useEffect(() => {
    if (canTransferDevice.get()) {
      if ('destiny_name' in textFieldsRef.current) {
        const item = textFieldsRef.current['destiny_name'];

        if (item) {
          // eslint-disable-next-line functional/immutable-data
          if (deviceName) {
            // eslint-disable-next-line functional/immutable-data
            item['value'] = deviceName;
          }
        }
      }
    }
  }, [canTransferDevice])

  const clickOutModalClose = (event, reason) => {
    if (reason !== 'backdropClick') {
      openState.set(false)
      isSending.set(false)
      isLoading.set(false)
    }
  };

  return (
    <>
      <Dialog
        open={openState.get()}
        onClose={clickOutModalClose}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          {`${title} ${(destinyNameState.get() ? destinyNameState.get() : deviceName) ?? ""}`}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmitForm)} >
          <DialogContent sx={{ padding: '0px 24px' }}>
            {(!isLoading.get() && errorCheckTransfer.get()) && (
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                <Typography>
                  {translate(errorCheckTransfer.value)}
                </Typography>
              </Box>
            )}
            {canTransferDevice.get() && (
              // eslint-disable-next-line react/jsx-no-useless-fragment
              <>
                {!isLoading.get() ?
                  fields.map((field) => {
                    if (field.type === 'select') {
                      return (
                        // eslint-disable-next-line react/jsx-key
                        <FormControl fullWidth size='small'>
                          <Controller
                            {...register('dispatchGroupId')}
                            name={'dispatchGroupId'}
                            control={control}
                            defaultValue={dispatchGroupId ? dispatchGroupId : undefined}
                            render={(props) => (
                              <Autocomplete
                                {...props}
                                id='filter-dispatch-group'
                                limitTags={2}
                                options={optionsFilter.filter((value) => value.name !== dispatchGroup)}
                                value={props.field.value && optionsFilter ? optionsFilter.find(element => element?.id === props.field.value) : null}
                                noOptionsText={translate('No options')}
                                clearText={translate('Clear')}
                                size='small'
                                loading={isLoading.get()}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                getOptionLabel={(option) => option.name ?? ''}
                                onChange={(_, type) => {
                                  props.field.onChange(type?.id);
                                  dispatchGroupIdSelected.set(type?.id ? type.id : null)
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label={translate('Dispatchs Groups')}
                                    error={!!errors.dispatchGroupId?.message}
                                    margin={'dense'}
                                  />
                                )} />
                            )} />
                        </FormControl>
                      )
                    }
                    if (field.type === 'date') {
                      return (
                        // eslint-disable-next-line react/jsx-key
                        <LocalizationProvider
                          dateAdapter={AdapterDayjs}
                          adapterLocale={'pt-br'}
                        >
                          <Controller
                            {...register('dateTobeReturned')}
                            control={control}
                            name={'dateTobeReturned'}
                            render={(props) => {
                              return <DateTimePicker
                                label={field.name}
                                format="DD/MM/YYYY HH:mm"
                                ampm={false}
                                ampmInClock={false}
                                defaultValue={dayjs(new Date())}
                                sx={{ margin: '6px 0px' }}
                                value={props.field.value ? dayjs(props.field.value) : null}
                                onChange={(newValue: Dayjs | null) => {
                                  props.field.onChange(newValue);
                                  return newValue;
                                }}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    size: 'small'
                                  }
                                }}
                              />;
                            }}
                          />
                        </LocalizationProvider>
                      )
                    }
                    return (
                      // eslint-disable-next-line react/jsx-key
                      <Controller
                        {...register('destinyName')}
                        name={'destinyName'}
                        control={control}
                        render={({
                          field: { onChange } }) => {
                          return (
                            <TextField
                              size={'small'}
                              defaultValue={deviceName}
                              key={field.name}
                              label={field.name}
                              required={field.required}
                              fullWidth
                              onChange={({ target: { value } }) => {
                                onChange(value)
                                if (setValue) setValue('destinyName', value)
                              }}
                              margin='dense'
                            />
                          )
                        }}
                      />
                    )
                  })
                  :
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress size={30} />
                  </Box>
                }
              </>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              padding: '16px 24px '
            }}
          >
            <Button
              disabled={isSending.get() || isLoading.get()}
              onClick={() => openState.set(false)}
              variant='outlined'
            >
              <Typography variant='button'>
                {translate('Cancel')}
              </Typography>
            </Button>
            {canTransferDevice.get() && (
              <LoadingButton
                type='submit'
                loading={isSending.get()}
                variant='contained'
                disabled={isSending.get() || isLoading.get()}
              >
                {translate('Submit')}
              </LoadingButton>
            )}

            {(!canTransferDevice.get() && isShowCancelScheduleButton.get()) && (
              <LoadingButton
                disabled={isSending.get() || isLoading.get()}
                onClick={onCancelScheduleTransfer}
                variant='contained'
                loading={isSending.get()}
              >
                {translate('Cancel Appointment')}
              </LoadingButton>
            )}
          </DialogActions>
        </form >
      </Dialog>

      {/* Dialog de Confirmação */}
      <Dialog
        open={showConfirmDialog.get()}
        onClose={handleCancelConfirm}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>
          {translate('Confirmar Transferência')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {translate('Este dispositivo está relacionado a uma escala, deseja proceder mesmo assim?')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleCancelConfirm}
            variant='outlined'
            disabled={isSending.get()}
          >
            <Typography variant='button'>
              {translate('Não')}
            </Typography>
          </Button>
          <LoadingButton
            onClick={handleConfirmTransfer}
            variant='contained'
            loading={isSending.get()}
          >
            {translate('Sim, Proceder')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}